import * as lodash from 'lodash';

/**
 * Filter's data using values of filter controls and search query
 */
export class FilterController<T, U> {
  private filterData: Partial<U> = {};
  private searchQuery: string[] = [];

  /**
   * Combines user's "filterPredicate" with build-in "searchPredicate" using current state of controller
   * @param {T} data to check
   * @return {boolean} if data meets filter conditions
   */
  public predicate: (data: T) => boolean = () => true;

  /**
   * @param {string}   searchPaths     paths to fields that will be checked for search query
   * @param {function} filterPredicate custom function used fo filter data; filters param is used to pass filter controls from
   *                                   current state
   */
  public constructor(
    /**
     * Paths to fields of data accessed by "searchPredicate"
     */
    private readonly searchPaths: string[],
    /**
     * User-defined search predicate,
     * check if all elements of "query" are present in any "data" fields accessed by "searchPaths"
     * @param {T}        data
     * @param {string[]} query
     * @return {boolean}
     */
    private readonly filterPredicate: (data: T, filters: Partial<U>) => boolean
  ) {}

  /**
   * Filters data using current state
   * @param {T[]} data
   * @return {T[]}
   */
  public filter(data: T[]): T[] {
    return data.filter(this.predicate);
  }

  /**
   * Update controller state by setting searched phrase
   * @param {string} value
   */
  public setSearchQuery(value: string): void {
    const sanitized = value.trim().toLocaleLowerCase();
    this.searchQuery = sanitized ? sanitized.split(/\s+/) : [];
    this.updatePredicate();
  }

  /**
   * Update controller state by setting value of filter controls
   * @param {string} value
   */
  public setFilters(value: Partial<U> | null): void {
    this.filterData = value ?? {};
    this.updatePredicate();
  }

  /**
   * Sets final predicate by combining build-in "searchPredicate", user's "filterPredicate" and current state of controller
   */
  private updatePredicate(): void {
    this.predicate = (x) => this.searchPredicate(x, this.searchQuery) && this.filterPredicate(x, this.filterData);
  }

  /**
   * Hard-coded search predicate, check if all elements of "query" are present in any "data" fields accessed by "searchPaths"
   * @param {T}        data
   * @param {string[]} query
   * @return {boolean}
   */
  private searchPredicate(data: T, query: string[]): boolean {
    if (!query.length) return true;

    for (const searchKey of query) {
      let match = false;

      for (const path of this.searchPaths) {
        let field = lodash.get(data, path);

        if (field) {
          // HACK: handling value tables
          if (field.value) field = field.value;

          const fieldValue: string = field.toString().toLocaleLowerCase();

          if (fieldValue.includes(searchKey)) {
            match = true;
            break;
          }
        }
      }
      if (!match) return false;
    }
    return true;
  }
}
